import React, { useEffect, useRef } from 'react'
import { SITE } from 'config'
import { getUser } from 'services/auth.service'
import { useI18n } from 'i18n'

export const salesMailto = (t) => {
  const user = getUser()
  const subject = t('trial.mailSubject', { company: user?.clientName || '' })
  const body = t('trial.mailBody', { name: user?.firstName || '', email: user?.email || '' })
  return `mailto:${SITE.contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

const isLimited = (usage) => usage && usage.limit !== null

/**
 * Trial progress ("2 of 3 free shipments left") or, once used up, the call to
 * talk to sales. Renders nothing for clients without a shipment limit.
 */
const TrialBanner = ({ usage }) => {
  const { t } = useI18n()
  if (!isLimited(usage)) return null
  const { used, limit, remaining, reached } = usage
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 100

  return (
    <section aria-label={t('trial.label')}
      className={`flex flex-col gap-4 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5 ${reached ? 'border-status-review/40 bg-status-review/5' : 'border-brand/20 bg-brand/5'}`}>
      <div className='min-w-0 flex-1'>
        <p className='text-sm font-semibold text-slate-800'>
          {reached ? t('trial.reachedTitle', { limit }) : t('trial.remaining', { count: remaining, limit })}
        </p>
        <p className='mt-0.5 text-sm text-slate-600'>{reached ? t('trial.reachedBody') : t('trial.body')}</p>
        <div className='mt-3 h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-slate-200' role='progressbar'
          aria-valuemin={0} aria-valuemax={limit} aria-valuenow={used} aria-label={t('trial.used', { used, limit })}>
          <div className={`h-full rounded-full ${reached ? 'bg-status-review' : 'bg-brand'}`} style={{ width: `${pct}%` }} />
        </div>
        <p className='mt-1 text-xs text-slate-500'>{t('trial.used', { used, limit })}</p>
      </div>
      <a href={salesMailto(t)}
        className={`shrink-0 rounded-lg px-4 py-2 text-center text-sm font-semibold ${reached ? 'bg-accent text-ink hover:bg-accent-600' : 'text-brand ring-1 ring-brand/30 hover:bg-brand/10'}`}>
        {t('trial.talkToSales')}
      </a>
    </section>
  )
}

/**
 * Modal shown when the API refuses a shipment because the trial is used up.
 */
export const UpgradeDialog = ({ open, onClose, limit }) => {
  const { t } = useI18n()
  const primary = useRef(null)
  useEffect(() => {
    if (!open) return
    primary.current?.focus()
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])
  if (!open) return null
  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4' onClick={onClose}>
      <div role='dialog' aria-modal='true' aria-labelledby='upgrade-title' onClick={(e) => e.stopPropagation()}
        className='w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl'>
        <h2 id='upgrade-title' className='text-lg font-bold text-slate-800'>{t('trial.reachedTitle', { limit })}</h2>
        <p className='mt-2 text-sm text-slate-600'>{t('trial.dialogBody')}</p>
        <ul className='mt-4 space-y-1 text-sm text-slate-600'>
          <li>• {t('trial.keepData')}</li>
          <li>• {t('trial.fastAnswer')}</li>
        </ul>
        <div className='mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
          <button type='button' onClick={onClose} className='rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100'>{t('trial.later')}</button>
          <a ref={primary} href={salesMailto(t)} className='rounded-lg bg-accent px-4 py-2 text-center text-sm font-semibold text-ink hover:bg-accent-600'>{t('trial.talkToSales')}</a>
        </div>
      </div>
    </div>
  )
}

export default TrialBanner
